import React from 'react';
import { IconButton } from '../../../icons';
import { IconType } from '../../../icons/Icon';
import {
  PdfStaticTextComponent,
  PdfComponentType,
  PdfFieldComponent,
  PdfTableComponent,
  PdfImageComponent,
  PdfImageFit,
  PdfBorderStyle,
  PdfTextAlign,
  PdfVerticalAlign,
  PdfTableType,
  PdfBarcodeComponent,
} from '../PdfComponent';
import {
  getDefaultTableColumnsForTableType,
  PdfEditorPreviewPage,
  usePdfTemplateContext,
} from '../PdfTemplateEditorContext';
import { DropdownSelect } from '../../../components/Dropdown';
import { PdfRepeat } from '../PdfComponent';
import { v4 } from 'uuid';
import {
  BarcodeExportConfigurationField,
  BarcodeExportConfigurationFormat,
} from '../../../../types/barcode_export_configuration';

export default function PdfTemplateEditorToolbar<T>() {
  const {
    components,
    setComponents,
    setSelectedId,
    dataKeys,
    tableProviders,
    editorSettings,
    updateEditorSettings,
    documentProperties,
  } = usePdfTemplateContext<T>();

  const addStaticText = () => {
    const newComponent: PdfStaticTextComponent = {
      id: v4(),
      border: { enabled: false, width: 0.5, style: PdfBorderStyle.solid, color: '#000000' },
      type: PdfComponentType.staticText,
      x: 20,
      y: 20,
      width: 40,
      height: 10,
      text: 'Static text',
      repeatMode: PdfRepeat.always,
      align: PdfTextAlign.left,
      valign: PdfVerticalAlign.top,
    };
    setComponents([newComponent, ...components]);
    setSelectedId(newComponent.id);
  };

  const addField = () => {
    const newComponent: PdfFieldComponent = {
      id: v4(),
      border: { enabled: false, width: 0.5, style: PdfBorderStyle.solid, color: '#000000' },
      type: PdfComponentType.field,
      x: 20,
      y: 20,
      width: 40,
      height: 10,
      key: dataKeys[0],
      repeatMode: PdfRepeat.always,
      align: PdfTextAlign.left,
      valign: PdfVerticalAlign.top,
    };
    setComponents([newComponent, ...components]);
    setSelectedId(newComponent.id);
  };

  const addTableRegion = (tableType: PdfTableType) => {
    const newComponent: PdfTableComponent = {
      repeatMode: PdfRepeat.always,
      tableType,
      id: v4(),
      type: PdfComponentType.table,
      x: 10,
      y: 40,
      width: 190,
      height: 140,
      sorting: [],
      rowHeight: 8,
      header: true,
      borderCellsHorizontal: true,
      borderCellsVertical: true,
      border: { enabled: true, width: 0.5, style: PdfBorderStyle.solid, color: '#000000' },
      columns: getDefaultTableColumnsForTableType(tableType),
    };
    setComponents([newComponent, ...components]);
    setSelectedId(newComponent.id);
  };

  const addImage = () => {
    const newComponent: PdfImageComponent = {
      id: v4(),
      type: PdfComponentType.image,
      x: 20,
      y: 60,
      width: 50,
      height: 30,
      src: undefined,
      fit: PdfImageFit.contain,
      opacity: 1,
      repeat: true,
      repeatMode: PdfRepeat.always,
    };
    setComponents([newComponent, ...components]);
    setSelectedId(newComponent.id);
  };

  const addBarcode = () => {
    const newComponent: PdfBarcodeComponent = {
      id: v4(),
      type: PdfComponentType.barcode,
      x: 20,
      y: 100,
      width: 50,
      height: 20,
      key: dataKeys[0],
      entry: {
        format: BarcodeExportConfigurationFormat.code128,
        height: 20,
        horizontalMargin: 2,
        verticalMargin: 2,
        includeText: true,
        field: BarcodeExportConfigurationField.productNumber,
        id: v4(),
      },
      repeatMode: PdfRepeat.always,
    };
    setComponents([newComponent, ...components]);
    setSelectedId(newComponent.id);
  };

  return (
    <div className='pointer-events-auto sticky bottom-4 left-0 right-0 z-40 flex w-full justify-center'>
      <div className='flex items-center gap-2 rounded-3 bg-white p-2 shadow-default'>
        <IconButton type={IconType.type01} text={'Static Text'} onClick={addStaticText} />
        {!!dataKeys.length && <IconButton type={IconType.textInput} text={'Field'} onClick={addField} />}
        {!!tableProviders.length && (
          <IconButton
            type={IconType.columns03}
            text={'Table'}
            onClick={() => addTableRegion(tableProviders[0].tableType)}
          />
        )}
        <IconButton type={IconType.imagePlus} text={'Image'} onClick={addImage} />
        <IconButton type={IconType.activity} text={'Barcode'} onClick={addBarcode} />
        <div className='mx-1 h-6 w-px bg-gray-200' />
        <div className='flex gap-2'>
          <DropdownSelect
            label={'Preview page'}
            items={Object.values(PdfEditorPreviewPage)}
            value={editorSettings.previewPage}
            onSelect={previewPage => updateEditorSettings({ previewPage })}
            textBuilder={v => v}
          />
        </div>
      </div>
    </div>
  );
}
